import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getProductListDataView } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import parseImageUrl from "@insite/mobius/utilities/parseImageUrl";
import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    background = "background",
    desktop_image = "desktop_image",
    tablet_image = "tablet_image",
    mobile_image = "mobile_image",
    backgroundColor = "backgroundColor",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.background]: "image" | "color";
        [fields.desktop_image]: string;
        [fields.tablet_image]: string;
        [fields.mobile_image]: string;
        [fields.backgroundColor]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        productsDataView: getProductListDataView(state),
    };
};
type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const NISProductListBackgroundContainerTaxonomy: React.FC<Props> = ({ id, fields, productsDataView }) => {
    const zoneRef = useRef<HTMLDivElement>(null);
    const [isEmpty, setIsEmpty] = useState(false);
    const products = productsDataView?.value;
    useEffect(() => {
        const zoneEl = zoneRef.current;
        if (zoneEl) {
            const hasRealContent =
                zoneEl.innerText.trim() !== "" ||
                Array.from(zoneEl.children).some(child => child instanceof HTMLElement && child.offsetHeight > 0);
            setIsEmpty(!hasRealContent);
        }
    }, [products]);
    if (isEmpty) {
        return null;
    }
    const backgroundStyles =
        fields.background === "image"
            ? `background-image: url(${parseImageUrl(fields.desktop_image).url}) !important;
                background-size: cover;
    background-repeat: no-repeat;
               `
            : `background-color: ${fields.backgroundColor};`;
    const container = {
        css: css`
            // direction: ltr;
            padding: 40px 64px 39px 64px;
            display: flex;
            flex-direction: column;
            gap: 16px;
            border-bottom: 1px solid #e0dfe0;
            max-width: 1800px;
            margin-left: auto;
            margin-right: auto;
            :has(> div:empty) {
                display: none;
            }
            > div[class^="Wrapper"]:first-child {
                display: flex;
                flex-direction: column;
                gap: 16px;
            }
            ${backgroundStyles};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 32px 16px 31px 16px;
                        background-image: url(${parseImageUrl(fields.mobile_image).url}) !important;
                    `,
                    css`
                        padding: 32px 16px 31px 16px;
                        background-image: url(${parseImageUrl(fields.mobile_image).url}) !important;
                    `,
                    css`
                        padding: 32px 16px 31px 16px;
                        background-image: url(${parseImageUrl(fields.tablet_image).url}) !important;
                    `,
                    null,
                    null,
                ])}
        `,
    };
    const containerempty = {
        css: css`
            padding: 40px 64px;
            border-bottom: 1px solid #e0dfe0;
            max-width: 1800px;
            margin-left: auto;
            margin-right: auto;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 32px 16px;
                    `,
                    css`
                        padding: 32px 16px;
                    `,
                    css`
                        padding: 40px 16px;
                    `,
                    null,
                    null,
                ])}
        `,
    };

    return (
        <StyledWrapper {...(products?.length === 0 ? containerempty : container)} className="plpBackgroundContainer">
            <StyledWrapper ref={zoneRef}>
                <Zone contentId={id} zoneName="content" />
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(NISProductListBackgroundContainerTaxonomy),
    definition: {
        displayName: "NIS Plp Background Container Taxonomy",
        group: "Common",
        fieldDefinitions: [
            {
                fieldType: "Contextual",
                name: fields.background,
                displayName: "Background",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Image", value: "image" },
                    { displayName: "Color", value: "color" },
                ],
                defaultValue: "color",
                hideEmptyOption: true,
            },
            {
                fieldType: "Contextual",
                name: fields.desktop_image,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                displayName: "Desktop_Image",
                isVisible: widget => widget.fields.background === "image",
            },
            {
                fieldType: "Contextual",
                name: fields.tablet_image,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                displayName: "Tablet_Image",
                isVisible: widget => widget.fields.background === "image",
            },
            {
                fieldType: "Contextual",
                name: fields.mobile_image,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                displayName: "Mobile_Image",
                isVisible: widget => widget.fields.background === "image",
            },
            {
                fieldType: "Contextual",
                name: fields.backgroundColor,
                editorTemplate: "ColorPickerField",
                displayName: "Color",
                defaultValue: "#F2F2F2",
                isVisible: widget => widget.fields.background === "color",
            },
        ],
    },
};

export default widgetModule;
